import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme as useMuiTheme } from '@mui/material/styles';

// RTL languages
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

/**
 * Hook to manage RTL (Right-to-Left) text direction
 * Automatically updates document direction based on current language
 * 
 * @returns {object} Object containing isRTL boolean and direction string
 * 
 * @example
 * const { isRTL, direction } = useRTL();
 * 
 * return (
 *   <Box sx={{ direction }}>
 *     {isRTL ? 'RTL Content' : 'LTR Content'}
 *   </Box>
 * );
 */
export const useRTL = () => {
  const { i18n } = useTranslation();
  const theme = useMuiTheme();
  
  const currentLanguage = i18n.language;
  const isRTL = RTL_LANGUAGES.includes(currentLanguage);
  const direction = isRTL ? 'rtl' : 'ltr';
  
  useEffect(() => {
    // Update document direction
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;
    
    // Update body direction for older browsers
    document.body.dir = direction;
    
    // Add RTL class for custom CSS
    if (isRTL) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [direction, currentLanguage, isRTL]);
  
  return {
    isRTL,
    direction,
    language: currentLanguage,
  };
};